import { createContext, useContext, useLayoutEffect, useSyncExternalStore } from "react";
import {
  FLOATING_TAB_BAR_BOTTOM_GAP_PX,
  FLOATING_TAB_BAR_HEIGHT_PX,
} from "#floating-tab-bar-metrics";

/** How far a scene's floating tabs (their platter and the gutter below it) reach above the strip. */
export const PRIMARY_NAVIGATION_STRIP_FLOATING_TABS_HEIGHT_PX =
  FLOATING_TAB_BAR_HEIGHT_PX + FLOATING_TAB_BAR_BOTTOM_GAP_PX;

export type PrimaryNavigationStripFloatingTabsRegistry = {
  claim: () => () => void;
  getHasFloatingTabs: () => boolean;
  subscribe: (listener: () => void) => () => void;
};

/**
 * Whether the focused scene floats its own tabs above the strip (Browse's category tabs, a
 * morpheme's Details tabs), so app-shell overlays anchored to the bottom can rest above them.
 * Reference-counted like the fade-reach claim: overlapping mounts during a transition must not
 * drop a still-active claim.
 */
export function createPrimaryNavigationStripFloatingTabsRegistry(): PrimaryNavigationStripFloatingTabsRegistry {
  const claims = new Set<number>();
  let nextId = 0;
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };
  return {
    claim() {
      const id = ++nextId;
      claims.add(id);
      notify();
      return () => {
        if (claims.delete(id)) {
          notify();
        }
      };
    },
    getHasFloatingTabs: () => claims.size > 0,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const INERT_REGISTRY: PrimaryNavigationStripFloatingTabsRegistry = {
  claim: () => () => undefined,
  getHasFloatingTabs: () => false,
  subscribe: () => () => undefined,
};

export const PrimaryNavigationStripFloatingTabsContext =
  createContext<PrimaryNavigationStripFloatingTabsRegistry>(INERT_REGISTRY);

/** Whether the focused scene currently floats tabs above the strip. */
export function useHasPrimaryNavigationStripFloatingTabs(): boolean {
  const registry = useContext(PrimaryNavigationStripFloatingTabsContext);
  return useSyncExternalStore(
    registry.subscribe,
    registry.getHasFloatingTabs,
    registry.getHasFloatingTabs,
  );
}

/**
 * While `isActive` holds, report that this scene floats tabs above the strip. Callers must already
 * gate on navigation focus when inactive scenes stay mounted.
 */
export function usePrimaryNavigationStripFloatingTabs(isActive: boolean): void {
  const registry = useContext(PrimaryNavigationStripFloatingTabsContext);
  useLayoutEffect(() => {
    if (!isActive) {
      return;
    }
    return registry.claim();
  }, [isActive, registry]);
}
